import { useMemo, useState } from 'react'
import { Link, Navigate, useNavigate } from '@tanstack/react-router'
import { Button, Dialog, Icon, SlideOver, TextField } from '@bloo-team/ui'
import { ArrowLeft, Play, Search } from '@bloo-team/ui/icons'
import { PageStrip, getSession, hasRole, identityFor } from '@bloo-team/core-shell'
import type { EntityKind } from '@bloo-team/core-shell'
import { notificationForSave, notificationProblems, readNotificationSpec } from '@bloo-team/api-client'
import type { BlockKind, WorkbookBlock } from '@bloo-team/api-client'
import { detectionSignalsQuery } from '../lib/detections'
import {
  exportWorkbook,
  readSignalSpec,
  sameVersion,
  versionNumber,
  signalSpecComplete,
  useGenerateKb,
  useLockWorkbook,
  useRevertWorkbook,
  useSaveWorkbook,
  useWorkbookDoc,
  useWorkbookKb,
  useWorkbookVersionDoc,
  useWorkbookVersions,
  versionLabel,
  visibleBlocks,
} from '../lib/workbook-blocks'
import {
  draftBaseOf, draftOf, insertBlock, isDraftDirty, moveBlock, parameterProblems, patchBlock, removeBlock,
  withDraftSettings,
} from '../lib/workbook-draft'
import type { WorkbookDraft } from '../lib/workbook-draft'
import { isRunnableBlock, useWorkbookRun } from '../lib/workbook-run'
import { runModeCommitMessage, scheduleOf, withSchedule } from '../lib/workbook-schedule'
import type { RunSchedule } from '../lib/workbook-schedule'
import { BlocksTab } from '../components/workbook/BlocksTab'
import { HistoryTab } from '../components/workbook/HistoryTab'
import { SettingsTab } from '../components/workbook/SettingsTab'
import type { DraftSettings } from '../components/workbook/SettingsTab'

/** Where a way out to Search lands: a query, or Search's entity mode. */
type SearchTarget = { q: string } | { mode: 'entity'; type: EntityKind; value: string; window: '24h' }

/**
 * The workbook page, INSIDE Detections (/vantage/detections/workbook?wb=…),
 * design B · Tab (#205): an action bar over three tabs — Blocks, Settings,
 * History.
 *
 * - View | Edit is one toggle on the same page. The draft is made on the first
 *   switch to Edit and survives switching back to View until Save or Discard.
 * - Blocks: every block kind viewed and edited in place, run one or all, each
 *   run carrying the previous block's task id (lib/workbook-run.ts).
 * - Settings: Placement (folder / stage / profile) and Parameters join the
 *   Edit draft and save with it; the Run mode is live in View and saves at
 *   once on Apply, as its own version, onto the LATEST document (an open
 *   draft is kept, and later saves onto the refreshed doc).
 * - History: every version, newest first; `?version=` views an old one
 *   read-only across all tabs (blocks still run), and Revert makes its
 *   content a new latest version.
 *
 * Edit, Apply and Revert share one gate: not locked + the Security Engineer
 * role; Edit and Apply are also off while an old version is on screen.
 */

export type WorkbookTab = 'blocks' | 'settings' | 'history'

export interface EditorSearch {
  wb?: string
  /** Absent = Blocks. */
  tab?: Exclude<WorkbookTab, 'blocks'>
  /** Time travel: sent as `query_relative_time` on every block run (legacy `relative=`). */
  relative?: number
  /** An old version on screen, read-only — always a plain number in the URL (`?version=5`). */
  version?: number
}

export function validateEditorSearch(search: Record<string, unknown>): EditorSearch {
  const version = versionParamOf(search.version)
  return {
    ...(typeof search.wb === 'string' && search.wb ? { wb: search.wb } : {}),
    ...(search.tab === 'settings' || search.tab === 'history' ? { tab: search.tab } : {}),
    ...(relativeOf(search.relative) !== null ? { relative: relativeOf(search.relative) as number } : {}),
    ...(version !== null ? { version } : {}),
  }
}

/** `?version=`: a non-negative integer (a typed `'5'` or `'v5'` is read as 5); anything else is the latest. */
function versionParamOf(v: unknown): number | null {
  if (typeof v === 'number') return Number.isInteger(v) && v >= 0 ? v : null
  const m = typeof v === 'string' ? v.trim().match(/^v?(\d+)$/i) : null
  return m ? Number(m[1]) : null
}

/** The legacy console parses `relative=` as an integer; anything else is no time travel. */
function relativeOf(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && /^-?\d+$/.test(v.trim()) ? Number(v) : NaN
  return Number.isInteger(n) ? n : null
}

export interface WorkbookEditorPageProps {
  clusterId: string
  scopeId: string
  wb?: string
  tab?: EditorSearch['tab']
  relative?: number
  version?: number
}

export function WorkbookEditorPage({ clusterId, scopeId, wb, tab, relative, version: versionNo }: WorkbookEditorPageProps) {
  /** The hooks and labels take the version as a string ('5'); the URL carries the number. */
  const version = versionNo !== undefined ? String(versionNo) : undefined
  const navigate = useNavigate()
  const scope = { clusterId, scopeId }
  const active: WorkbookTab = tab ?? 'blocks'

  const docQ = useWorkbookDoc({ ...scope, id: wb ?? null })
  /** The latest saved document — every save builds on it. */
  const latest = docQ.data ?? null
  /** An old version is on screen: `?version=` names one that is not the latest. */
  const viewingOld = version !== undefined && latest !== null && !sameVersion(version, latest.version)
  const versionQ = useWorkbookVersionDoc({ ...scope, id: wb ?? null, version: viewingOld ? version : null })
  /** The document on screen. */
  const doc = viewingOld ? (versionQ.data ?? null) : latest
  const loading = docQ.isPending || (viewingOld && versionQ.isPending)
  const failed = docQ.isError || (viewingOld && versionQ.isError)
  const versionsQ = useWorkbookVersions({ ...scope, id: active === 'history' ? (wb ?? null) : null })
  const lock = useLockWorkbook(scope)
  const generateKb = useGenerateKb(scope)
  const save = useSaveWorkbook(scope)
  /** The run-mode Apply: its own save, so it never shares state with the draft's. */
  const applyRun = useSaveWorkbook(scope)
  const revert = useRevertWorkbook(scope)

  /** Non-null once Edit was entered; kept across the View toggle until Save / Discard. */
  const [draft, setDraft] = useState<WorkbookDraft | null>(null)
  const [mode, setMode] = useState<'view' | 'edit'>('view')
  const [showDocs, setShowDocs] = useState(false)
  const [exportErr, setExportErr] = useState<string | null>(null)
  const [commitOpen, setCommitOpen] = useState(false)
  const [commitMsg, setCommitMsg] = useState('')
  const [revertTarget, setRevertTarget] = useState<string | null>(null)

  const latestBlocks = useMemo(() => (latest ? visibleBlocks(latest) : []), [latest])
  const base = useMemo(() => (latest ? draftBaseOf(latest, latestBlocks) : null), [latest, latestBlocks])
  const shownBlocks = useMemo(() => (viewingOld && doc ? visibleBlocks(doc) : null), [viewingOld, doc])
  const blocks = shownBlocks ?? (draft ? draft.blocks : latestBlocks)
  const editing = mode === 'edit' && draft !== null && !viewingOld
  const dirty = base !== null && isDraftDirty(base, draft)
  const shownSettings: DraftSettings | null = viewingOld ? (doc ? draftBaseOf(doc, []) : null) : (draft ?? base)

  const host = useMemo(() => (doc ? { id: doc.id, path: doc.path, name: doc.name } : null), [doc])
  /** Parameter values for runs: the view bar's entries over each stored value. */
  const [paramEdits, setParamEdits] = useState<Record<string, string>>({})
  const paramValues = useMemo(
    () => Object.fromEntries((doc?.parameters ?? []).map((p) => [p.name, paramEdits[p.name] ?? p.value])),
    [doc, paramEdits],
  )
  const run = useWorkbookRun({ ...scope, workbook: host, blocks, parameters: paramValues, relativeTime: relative ?? null })

  const session = getSession()
  const identity = session ? identityFor(session, clusterId, scopeId) : null
  const isContentManager =
    identity !== null && (identity.userType === 'ContentManager' || identity.userType === 'Root')
  const kbQ = useWorkbookKb({ ...scope, doc, enabled: showDocs })
  /** The one gate for Edit, the run-mode Apply and Revert. */
  const mayChange = latest !== null && !latest.is_locked && identity !== null && hasRole(identity, ['Security Engineer'])
  const canEdit = mayChange && !viewingOld
  const changeReason = !latest
    ? null
    : latest.is_locked
      ? 'Locked.'
      : !mayChange
        ? 'Needs the Security Engineer role.'
        : viewingOld
          ? 'Viewing an old version — back to latest to change it.'
          : null

  const signalsComplete = !draft || draft.blocks.every((b) => b.type !== 'signal' || signalSpecComplete(readSignalSpec(b)))
  const notificationsOk =
    !draft || draft.blocks.every((b) => b.type !== 'notification' || notificationProblems(readNotificationSpec(b)).length === 0)
  const nameOk = !draft || draft.name.trim() !== ''
  const paramIssues = draft ? parameterProblems(draft.parameters) : []
  const paramsOk = paramIssues.length === 0

  const setTab = (next: WorkbookTab) => {
    void navigate({
      to: '.',
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: next === 'blocks' ? undefined : next }),
    })
  }
  /** `?version=` on (an old version, read-only) or off (the latest). */
  const setVersion = (v: string | undefined) => {
    run.reset()
    const n = v !== undefined ? versionNumber(v) : null
    void navigate({ to: '.', search: (prev: Record<string, unknown>) => ({ ...prev, version: n ?? undefined }) })
  }
  const applyRunMode = (next: RunSchedule) => {
    if (!latest || !canEdit) return
    applyRun.mutate({
      // Onto the LATEST saved doc, never the draft: only type / cron / cron_obj / schedule_timezone change.
      doc: withSchedule(latest, next),
      blocks: latest.blocks,
      commitMsg: runModeCommitMessage(scheduleOf(latest).mode, next),
    })
  }
  const confirmRevert = () => {
    if (!latest || revertTarget === null) return
    revert.mutate(
      { id: latest.id, version: revertTarget },
      {
        onSuccess: () => {
          setRevertTarget(null)
          setVersion(undefined)
        },
      },
    )
  }
  const toEdit = () => {
    if (!base || !canEdit) return
    if (!draft) setDraft(draftOf(base))
    setMode('edit')
  }
  const discard = () => {
    setDraft(null)
    setMode('view')
    run.reset()
  }
  /** Structural edits shift block indexes — index-keyed results would misalign. */
  const restructure = (next: WorkbookBlock[]) => {
    setDraft((d) => (d ? { ...d, blocks: next } : d))
    run.reset()
  }
  /**
   * Every way out to Search — pivots, entity links, "open in Search", the
   * Signals button — goes in the same tab, so an unsaved draft would be lost:
   * with one open, confirm first.
   */
  const [leaving, setLeaving] = useState<SearchTarget | null>(null)
  const goSearch = (target: SearchTarget) =>
    void navigate({ to: '/$clusterId/$scopeId/fabric/search', params: { clusterId, scopeId }, search: target as never })
  const toSearch = (target: SearchTarget) => (dirty ? setLeaving(target) : goSearch(target))
  const pivotQueryToSearch = (q: string) => toSearch({ q })
  const entityToSearch = (type: EntityKind, value: string) => toSearch({ mode: 'entity', type, value, window: '24h' })

  // The library IS the list — no picker here. Bare visits go back to it.
  if (!wb) {
    return <Navigate to="/$clusterId/$scopeId/vantage/detections" params={{ clusterId, scopeId }} />
  }

  const schedule = doc ? scheduleOf(doc) : null

  return (
    <div>
      <PageStrip title="Vantage · Detections · Workbook" meta={`scope ${scopeId}`} />

      {loading ? (
        <div className="hud-panel wb-pad">
          <p className="hud-unit" role="status">Loading the workbook…</p>
        </div>
      ) : failed || !doc || !latest ? (
        <div className="hud-panel wb-pad">
          <div className="bui-alert bui-alert-error" role="alert">
            <span className="bui-alert-mark">✕</span>
            <span>
              {docQ.isError
                ? 'The workbook could not be loaded.'
                : latest && viewingOld
                  ? `Version ${versionLabel(version)} could not be loaded.`
                  : 'No workbook with this id.'}
            </span>
          </div>
          {latest && viewingOld ? (
            <button type="button" className="bui-btn bui-btn-ghost bui-btn-sm" onClick={() => setVersion(undefined)}>
              back to latest ({versionLabel(latest.version)})
            </button>
          ) : null}
        </div>
      ) : (
        <>
          {/* Identity, mode and every verb, always in reach. */}
          <div className="hunt-bar">
            <Link
              to="/$clusterId/$scopeId/vantage/detections"
              params={{ clusterId, scopeId }}
              className="bui-btn bui-btn-ghost bui-btn-sm"
              aria-label="Back to the detection library"
            >
              <Icon icon={ArrowLeft} size={13} />
            </Link>
            {editing ? (
              <TextField
                aria-label="Workbook name"
                className="wb-name-field"
                value={draft.name}
                onChange={(v) => setDraft((d) => (d ? { ...d, name: v } : d))}
              />
            ) : (
              <span className="hunt-bar-name">{(viewingOld ? null : draft)?.name ?? doc.name}</span>
            )}
            <span className="hunt-bar-meta">
              {doc.path || '/'} · {versionLabel(doc.version)}
              {latest.is_locked ? ' · locked' : ''}
            </span>
            {viewingOld ? (
              <span className="wb-viewing" role="status">
                <span className="bui-chip bui-chip-warn">viewing {versionLabel(version)}</span>
                <span aria-hidden="true">·</span>
                <button type="button" className="bui-btn bui-btn-ghost bui-btn-sm" onClick={() => setVersion(undefined)}>
                  back to latest ({versionLabel(latest.version)})
                </button>
                <button
                  type="button"
                  className="bui-btn bui-btn-outline bui-btn-sm"
                  disabled={!mayChange}
                  title={mayChange ? undefined : (changeReason ?? undefined)}
                  onClick={() => setRevertTarget(version ?? null)}
                >
                  Revert…
                </button>
              </span>
            ) : null}
            {dirty ? <span className="bui-chip bui-chip-warn">unsaved changes</span> : null}
            {relative !== undefined ? (
              <span className="bui-chip bui-chip-accent" title="Runs send query_relative_time">time travel · relative {relative}</span>
            ) : null}
            <span className="wb-summary" aria-label="Settings summary">
              <span className="strip-stat">
                <span className="strip-stat-label">stage</span>
                <span className="strip-stat-value">{doc.stage || '—'}</span>
              </span>
              <span className="strip-stat">
                <span className="strip-stat-label">run</span>
                <span className="strip-stat-value">{schedule ? schedule.mode : '—'}</span>
              </span>
              <span className="strip-stat">
                <span className="strip-stat-label">params</span>
                <span className="strip-stat-value">{doc.parameters.length}</span>
              </span>
            </span>
            <span className="spacer" />
            <div className="bui-toggles" role="group" aria-label="Mode">
              <button type="button" className="bui-toggle" aria-pressed={!editing} onClick={() => setMode('view')}>
                View
              </button>
              <button
                type="button"
                className="bui-toggle"
                aria-pressed={editing}
                disabled={!canEdit}
                title={canEdit ? undefined : (changeReason ?? undefined)}
                onClick={toEdit}
              >
                Edit
              </button>
            </div>
            {editing ? (
              <>
                {!signalsComplete ? <span className="hunt-bar-meta">signal block incomplete</span> : null}
                {!notificationsOk ? <span className="hunt-bar-meta">notification block incomplete</span> : null}
                {!paramsOk ? <span className="hunt-bar-meta">{paramIssues.join(' · ')}</span> : null}
                <button type="button" className="bui-btn bui-btn-ghost bui-btn-sm" disabled={!draft} onClick={discard}>
                  Discard
                </button>
                <button
                  type="button"
                  className="bui-btn bui-btn-solid bui-btn-sm"
                  disabled={!dirty || save.isPending || !nameOk || !signalsComplete || !notificationsOk || !paramsOk}
                  onClick={() => setCommitOpen(true)}
                >
                  Save…
                </button>
              </>
            ) : (
              <>
                {blocks.some(isRunnableBlock) ? (
                  <button
                    type="button"
                    className="bui-btn bui-btn-solid bui-btn-sm"
                    disabled={run.runningAll}
                    onClick={run.runAll}
                  >
                    <Icon icon={Play} size={13} /> {run.runningAll ? 'Running…' : 'Run all'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="bui-btn bui-btn-ghost bui-btn-sm"
                  aria-pressed={showDocs}
                  onClick={() => setShowDocs((v) => !v)}
                >
                  Docs
                </button>
                <button
                  type="button"
                  className="bui-btn bui-btn-ghost bui-btn-sm"
                  onClick={() => {
                    setExportErr(null)
                    exportWorkbook({ ...scope, doc }).catch((e: unknown) =>
                      setExportErr(e instanceof Error ? e.message : 'export failed'),
                    )
                  }}
                >
                  Export
                </button>
                {isContentManager ? (
                  <button
                    type="button"
                    className="bui-btn bui-btn-ghost bui-btn-sm"
                    disabled={lock.isPending}
                    onClick={() => lock.mutate({ id: latest.id, lock: !latest.is_locked })}
                  >
                    {latest.is_locked ? 'Unlock' : 'Lock'}
                  </button>
                ) : null}
                <Link
                  to="/$clusterId/$scopeId/vantage/detections"
                  params={{ clusterId, scopeId }}
                  search={{ sel: doc.id }}
                  className="bui-btn bui-btn-ghost bui-btn-sm"
                >
                  In library
                </Link>
                <button
                  type="button"
                  className="bui-btn bui-btn-ghost bui-btn-sm"
                  onClick={() => pivotQueryToSearch(detectionSignalsQuery(doc.name))}
                >
                  <Icon icon={Search} size={13} /> Signals
                </button>
              </>
            )}
          </div>

          {exportErr ? (
            <div className="bui-alert bui-alert-error wb-gap-below" role="alert">
              <span className="bui-alert-mark">✕</span>
              <span>Export failed: {exportErr}</span>
            </div>
          ) : null}

          <div className="bui-tabs wb-tabs" role="tablist" aria-label="Workbook sections">
            <button type="button" role="tab" className="bui-tab" aria-selected={active === 'blocks'} onClick={() => setTab('blocks')}>
              Blocks <span className="n">{blocks.length}</span>
            </button>
            <button type="button" role="tab" className="bui-tab" aria-selected={active === 'settings'} onClick={() => setTab('settings')}>
              Settings
            </button>
            <button type="button" role="tab" className="bui-tab" aria-selected={active === 'history'} onClick={() => setTab('history')}>
              History
            </button>
          </div>

          <div role="tabpanel" aria-label={active} className="wb-panel">
            {active === 'blocks' ? (
              <div className="hud-stack wb-gap-hud">
                {editing ? (
                  <TextField
                    aria-label="Workbook description"
                    placeholder="description…"
                    value={draft.description}
                    onChange={(v) => setDraft((d) => (d ? { ...d, description: v } : d))}
                  />
                ) : ((viewingOld ? null : draft)?.description ?? doc.description) ? (
                  <p className="hud-unit wb-flush">{(viewingOld ? null : draft)?.description ?? doc.description}</p>
                ) : null}
                <BlocksTab
                  clusterId={clusterId}
                  scopeId={scopeId}
                  blocks={blocks}
                  editing={editing}
                  parameters={doc.parameters}
                  paramValues={paramValues}
                  onParamChange={(name, value) => setParamEdits((cur) => ({ ...cur, [name]: value }))}
                  run={run}
                  onPatch={(i, next) => setDraft((d) => (d ? { ...d, blocks: patchBlock(d.blocks, i, next) } : d))}
                  onInsert={(at, kind: BlockKind) => restructure(insertBlock(blocks, at, kind))}
                  onRemove={(i) => restructure(removeBlock(blocks, i))}
                  onMove={(i, dir) => restructure(moveBlock(blocks, i, dir))}
                  onPivot={pivotQueryToSearch}
                  onEntity={entityToSearch}
                />
              </div>
            ) : active === 'settings' ? (
              shownSettings ? (
                <SettingsTab
                  clusterId={clusterId}
                  scopeId={scopeId}
                  doc={doc}
                  settings={shownSettings}
                  editing={editing}
                  onSettings={(patch) => setDraft((d) => (d ? { ...d, ...patch } : d))}
                  runMode={{
                    enabled: canEdit,
                    reason: changeReason,
                    pending: applyRun.isPending,
                    error: applyRun.isError
                      ? applyRun.error instanceof Error ? applyRun.error.message : 'The run mode could not be saved.'
                      : null,
                    onApply: applyRunMode,
                  }}
                />
              ) : null
            ) : (
              <HistoryTab
                versions={versionsQ.data ?? []}
                isPending={versionsQ.isPending}
                isError={versionsQ.isError}
                viewing={viewingOld ? (version ?? null) : null}
                canRevert={mayChange}
                revertReason={changeReason}
                onView={(v) => setVersion(v)}
                onRevert={(v) => setRevertTarget(v)}
              />
            )}
          </div>
        </>
      )}

      <SlideOver
        open={showDocs}
        onClose={() => setShowDocs(false)}
        eyebrow="detection documentation"
        title={doc?.name ?? ''}
        actions={
          isContentManager && doc ? (
            <button
              type="button"
              className="bui-btn bui-btn-outline bui-btn-sm"
              disabled={generateKb.isPending || identity === null}
              onClick={() => {
                if (!identity || !doc) return
                generateKb.mutate({
                  doc,
                  user: {
                    name: identity.displayName,
                    email: identity.email,
                    role: identity.role,
                    userType: identity.userType,
                    licenseType: identity.tier,
                  },
                })
              }}
            >
              {generateKb.isPending ? 'requesting…' : 'Generate (AI)'}
            </button>
          ) : null
        }
      >
        {generateKb.isSuccess ? <p className="hud-unit mono">generation requested — reopen Docs shortly</p> : null}
        {kbQ.isPending ? (
          <p className="hud-unit" role="status">Loading documentation…</p>
        ) : kbQ.isError ? (
          <p className="hud-unit" role="status">Documentation could not be loaded.</p>
        ) : kbQ.data && (kbQ.data.kbData || kbQ.data.kbFileUrl) ? (
          <>
            {kbQ.data.kbData ? <p className="hud-unit wb-prewrap">{kbQ.data.kbData}</p> : null}
            {kbQ.data.kbFileUrl ? (
              <p className="hud-unit mono">
                <a href={kbQ.data.kbFileUrl} target="_blank" rel="noreferrer">open the generated document</a>
              </p>
            ) : null}
          </>
        ) : (
          <p className="hud-unit" role="status">No documentation yet{isContentManager ? ' — generate it above' : ''}.</p>
        )}
      </SlideOver>

      <Dialog
        open={commitOpen}
        onClose={() => setCommitOpen(false)}
        title="Save workbook"
        actions={
          <>
            <Button variant="ghost" tone="neutral" size="sm" onPress={() => setCommitOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              // Not while the latest doc refetches (after a run-mode Apply): the save builds on it.
              isDisabled={save.isPending || !latest || !draft || docQ.isFetching}
              onPress={() => {
                if (!latest || !draft) return
                save.mutate(
                  {
                    // Placement + parameters onto the CURRENT latest doc; the run mode is never in the draft.
                    doc: withDraftSettings(latest, draft),
                    // Legacy payload rule: per-row notifications save their consolidated fields empty.
                    blocks: draft.blocks.map(notificationForSave),
                    commitMsg: commitMsg.trim(),
                    name: draft.name.trim(),
                    description: draft.description,
                  },
                  {
                    onSuccess: () => {
                      setCommitOpen(false)
                      setCommitMsg('')
                      setDraft(null)
                      setMode('view')
                    },
                  },
                )
              }}
            >
              {save.isPending ? 'Saving…' : 'Save version'}
            </Button>
          </>
        }
      >
        <div className="hud-stack">
          <p className="hud-unit">Saving writes a new version; the current one stays in history.</p>
          <TextField label="commit message" placeholder="what changed and why" value={commitMsg} onChange={setCommitMsg} />
          {save.isError ? (
            <div className="bui-alert bui-alert-error" role="alert">
              <span className="bui-alert-mark">✕</span>
              <span>{save.error instanceof Error ? save.error.message : 'Save failed.'}</span>
            </div>
          ) : null}
        </div>
      </Dialog>

      <Dialog
        open={leaving !== null}
        onClose={() => setLeaving(null)}
        title="Discard unsaved changes?"
        actions={
          <>
            <Button variant="ghost" tone="neutral" size="sm" onPress={() => setLeaving(null)}>
              Keep editing
            </Button>
            <Button
              size="sm"
              tone="warn"
              onPress={() => {
                const target = leaving
                setLeaving(null)
                if (target) goSearch(target)
              }}
            >
              Discard and open Search
            </Button>
          </>
        }
      >
        <p className="hud-unit">Search opens in this tab, and this workbook&apos;s unsaved changes are lost. Save first to keep them.</p>
      </Dialog>

      <Dialog
        open={revertTarget !== null}
        onClose={() => setRevertTarget(null)}
        title={`Revert to ${versionLabel(revertTarget)}?`}
        actions={
          <>
            <Button variant="ghost" tone="neutral" size="sm" onPress={() => setRevertTarget(null)}>
              Cancel
            </Button>
            <Button size="sm" isDisabled={revert.isPending || !mayChange} onPress={confirmRevert}>
              {revert.isPending ? 'Reverting…' : 'Revert'}
            </Button>
          </>
        }
      >
        <div className="hud-stack">
          <p className="hud-unit">
            {versionLabel(revertTarget)}&apos;s blocks and settings become a new latest version. Every version stays in
            history.
          </p>
          {dirty ? <p className="hud-unit">Your unsaved draft is kept; saving it later writes over the reverted content.</p> : null}
          {revert.isError ? (
            <div className="bui-alert bui-alert-error" role="alert">
              <span className="bui-alert-mark">✕</span>
              <span>{revert.error instanceof Error ? revert.error.message : 'Revert failed.'}</span>
            </div>
          ) : null}
        </div>
      </Dialog>
    </div>
  )
}
